import React from 'react';
import { useTranslation } from 'react-i18next';
import HazardGlyph from './HazardGlyph';
import { parseMagnitudeDepth } from '../../../utils/gdacs/gdacsEvent';
import { stormClassLabel } from './hazardFacts';
import { hasDrawableGeometry } from '../../../utils/gdacs/hazardBounds';
import { parseGdacsIsoUtc, formatUtcDate, formatUtcDateTime, formatUtcLabel } from '../../../utils/gdacs/gdacsTime';

// Same colours as the map layers.
const WIND_SWATCH = { 60: '#f59e0b', 90: '#ea580c', 120: '#9a3412' };
const TRACK_SWATCH = '#16b84e';
const CONE_SWATCH = '#82c9ed';

const windBandsDrawn = (parsed) => {
  const found = new Set(Object.keys(parsed?.windSwath || {}).map(Number));
  (parsed?.windByTime || []).forEach((w) => Object.keys(w?.sets || {}).forEach((b) => found.add(Number(b))));
  return [60, 90, 120].filter((b) => found.has(b));
};
const PATH_STALE_MS = 6 * 3600 * 1000;

/**
 * The one tooltip of a hazard marker: everything known about the event, shown on the first click. Cyclones add the
 * path state (loading / error / stale), the Play clock and a small legend of what is drawn. `detail` is only passed to
 * the selected event: { parsed, status, clockState, pathUpdatedMs, pathMeta, onRetry }.
 */
export default function HazardTooltip({ event, isDark, detail = null }) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language || 'en';
  const muted = isDark ? 'text-gray-400' : 'text-[#6b7280]';
  const strong = isDark ? 'text-white' : 'text-[#282A29]';
  const unavailable = t('map.hazard.unavailableValue', 'Unavailable');
  const weekdays = t('map.hazard.weekdays', 'Sun,Mon,Tue,Wed,Thu,Fri,Sat').split(',');

  const isTC = event.eventType === 'TC';
  const isEQ = event.eventType === 'EQ';
  const isFL = event.eventType === 'FL';
  const parsed = detail?.parsed ?? null;
  const status = detail?.status ?? null;
  const clockState = detail?.clockState ?? null;
  const clockMs = detail?.clockMs ?? null;
  const pathUpdatedMs = detail?.pathUpdatedMs ?? null;
  const pathMeta = detail?.pathMeta ?? null;

  const updatedMs = parseGdacsIsoUtc(event.dateModified);
  const startMs = parseGdacsIsoUtc(event.fromDate);
  const endMs = parseGdacsIsoUtc(event.toDate);
  const { magnitude, depthKm } = parseMagnitudeDepth(event.severityData?.text);
  const mag = magnitude ?? (Number.isFinite(event.severityData?.value) ? event.severityData.value : null);
  const classText = stormClassLabel({ event, parsed, t });

  const Row = ({ label, children }) => (
    <div className="flex justify-between gap-3 py-0.5 text-sm">
      <span className={`shrink-0 ${muted}`}>{label}</span>
      <span className={`text-right font-medium ${strong}`}>{children}</span>
    </div>
  );

  // Play clock chip (cyclones; ended storms and static tracks show none): which frame the map is showing.
  let clockChip = null;
  let noWind = false;
  if (isTC && parsed && clockState && clockState.phase !== 'static') {
    const frame = clockMs != null ? formatUtcLabel(clockMs, weekdays) : null;
    if (clockState.phase === 'observed') {
      clockChip = frame ? `${t('map.hazard.observed', 'Observed')} · ${frame}` : t('map.hazard.observed', 'Observed');
    } else if (clockState.phase === 'moving') {
      clockChip = frame ? `${t('map.hazard.forecastAt', 'Forecast')} · ${frame}` : t('map.hazard.forecastAt', 'Forecast');
    } else if (clockState.phase === 'ended') {
      clockChip = t('map.hazard.forecastEnds', 'Forecast ends {{time}}', { time: formatUtcLabel(clockState.holdLabelMs, weekdays) });
    }
    const entry = clockState.windIndex != null ? parsed.windByTime[clockState.windIndex] : null;
    noWind = !!entry && Object.keys(entry.sets).length === 0;
  }

  const pathAgeMs = pathUpdatedMs != null ? Date.now() - pathUpdatedMs : null;
  const ended = isTC && !event.isCurrent;
  const pathStale = !ended && pathAgeMs != null && pathAgeMs > PATH_STALE_MS;   // an ended storm has no newer path to wait for
  const lastForecast = parsed?.track?.vertices?.filter((v) => v.phase === 'forecast' && v.timeMs != null).slice(-1)[0] ?? null;
  const emptyGeometry = !!parsed && !hasDrawableGeometry(parsed);
  const note = 'text-xs text-amber-600';

  return (
    <div role="dialog" aria-label={event.name} className="w-full">
      <div className={`mb-1.5 flex items-center gap-2 text-base font-semibold ${strong}`}>
        <HazardGlyph type={event.type} size={22} />
        <span className="min-w-0 break-words">{event.name}</span>
      </div>

      <div>
        {isTC && <Row label={t('map.hazard.class', 'Class')}>{classText || unavailable}</Row>}
        {isEQ && <Row label={t('map.hazard.magnitude', 'Magnitude')}>{mag ?? unavailable}</Row>}
        {isEQ && <Row label={t('map.hazard.depth', 'Depth')}>{depthKm != null ? `${depthKm} km` : unavailable}</Row>}
        {isEQ && <Row label={t('map.hazard.time', 'Time')}>{startMs != null ? formatUtcDateTime(startMs, locale) : unavailable}</Row>}
        {isFL && <Row label={t('map.hazard.start', 'Start')}>{startMs != null ? formatUtcDate(startMs, locale) : unavailable}</Row>}
        {isFL && <Row label={t('map.hazard.end', 'End')}>{endMs != null ? formatUtcDate(endMs, locale) : t('map.hazard.notReported', 'Not reported')}</Row>}
        {ended && (
          <Row label={t('map.hazard.status', 'Status')}>
            {endMs != null
              ? t('map.hazard.endedAt', 'Ended · last position {{time}}', { time: formatUtcDateTime(endMs, locale) })
              : t('map.hazard.ended', 'Ended')}
          </Row>
        )}
        {isTC && !ended && lastForecast && (
          <Row label={t('map.hazard.forecastUntil', 'Forecast until')}>{formatUtcDateTime(lastForecast.timeMs, locale)}</Row>
        )}
        <Row label={t('map.hazard.updated', 'Updated')}>{updatedMs != null ? formatUtcDateTime(updatedMs, locale) : unavailable}</Row>
      </div>

      {detail && (
        <div className="mt-2 space-y-1.5 border-t border-black/10 pt-2 dark:border-white/10">
          {isTC && clockChip && (
            <span className="inline-flex rounded-full bg-bloom-green/15 px-2 py-0.5 text-[11px] font-semibold text-bloom-green">{clockChip}</span>
          )}

          <div role="status" aria-live="polite" className="space-y-1.5">
            {status === 'loading' && <p className={`text-xs ${muted}`}>{t('map.hazard.loading', 'Loading the storm path…')}</p>}
            {status === 'error' && (
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs text-red-600">{t('map.hazard.error', "Couldn't load the storm path.")}</p>
                <button
                  type="button"
                  onClick={detail.onRetry}
                  className="min-h-[32px] rounded-md bg-bloom-green px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
                >
                  {t('map.hazard.retry', 'Retry')}
                </button>
              </div>
            )}
            {(status === 'unavailable' || (status === 'ready' && emptyGeometry)) && (
              <p className={`text-xs ${muted}`}>{t('map.hazard.unavailable', 'No storm path yet.')}</p>
            )}
          </div>

          {isTC && status === 'ready' && parsed && !emptyGeometry && (
            <>
              {pathUpdatedMs != null && !ended && (
                <p className={`text-xs ${pathStale ? 'text-amber-600' : muted}`}>
                  {pathStale
                    ? t('map.hazard.region.footerPathAge', 'Path updated {{time}} ({{hours}} h ago)', {
                      time: formatUtcDateTime(pathUpdatedMs, locale), hours: Math.floor(pathAgeMs / 3.6e6),
                    })
                    : t('map.hazard.region.footerPath', 'Path updated {{time}}', { time: formatUtcDateTime(pathUpdatedMs, locale) })}
                </p>
              )}
              {noWind && <p className={`${note} font-medium`}>{t('map.hazard.noWindAtTime', 'No wind area at this time')}</p>}
              {parsed.track.trackIncomplete && <p className={note}>{t('map.hazard.trackIncomplete', 'Storm path incomplete.')}</p>}
              {parsed.track.source === 'positions' && <p className={note}>{t('map.hazard.pathFromPositions', 'Path drawn from the forecast positions.')}</p>}
              {!parsed.cone && <p className={note}>{t('map.hazard.coneUnavailable', 'Uncertainty cone not published yet; wind areas only.')}</p>}
              {pathMeta?.refetchFailed && (
                <div className="flex flex-wrap items-center gap-2">
                  <p className={note}>
                    {t('map.hazard.pathRefreshFailed', "Couldn't refresh the storm path; showing the version from {{time}}.", {
                      time: pathUpdatedMs != null ? formatUtcDateTime(pathUpdatedMs, locale) : '',
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={detail.onRetry}
                    className="min-h-[32px] rounded-md bg-bloom-green px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
                  >
                    {t('map.hazard.retry', 'Retry')}
                  </button>
                </div>
              )}
              <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] ${muted}`}>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-0 w-4 border-t-[3px]" style={{ borderColor: TRACK_SWATCH }} />
                  {t('map.hazard.legend.track', 'Storm track (class, UTC time)')}
                </span>
                {parsed.cone && (
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-3 w-4 border border-dashed" style={{ borderColor: CONE_SWATCH, background: `${CONE_SWATCH}33` }} />
                    {t('map.hazard.legend.cone', 'Uncertainty cone')}
                  </span>
                )}
                {windBandsDrawn(parsed).length > 0 && (
                  <span className="inline-flex items-center gap-1">
                    {windBandsDrawn(parsed).map((b) => (
                      <span key={b} className="inline-block h-3 w-3" style={{ background: WIND_SWATCH[b] }} />
                    ))}
                    {t('map.hazard.legend.windBands', 'Wind ≥{{bands}} km/h', { bands: windBandsDrawn(parsed).join(' · ≥') })}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {isEQ && detail && status === 'ready' && parsed?.quake?.circle && (
        <p className={`mt-2 border-t border-black/10 pt-2 text-[11px] dark:border-white/10 ${muted}`}>
          {t('map.hazard.circleNote', 'The 100 km circle is a locator, not the impact extent.')}
        </p>
      )}
    </div>
  );
}
